import { useEffect, useCallback, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {MessageDTO} from "../models/dto/message-dto";

interface UseWebSocketWithTokenOptions {
    onOpen?: () => void;
    onClose?: () => void;
    onError?: (error: Event) => void;
    onMessage?: (message: MessageDTO) => void;
    reconnectInterval?: number;
}

interface UseWebSocketWithTokenResult {
    isConnected: boolean;
    closeConnection: () => void;
}

/**
 * Custom hook that connects to a WebSocket URL with token handling and
 * event listeners using `react-use-websocket`.
 *
 * `getToken` is invoked on initial connect and on every reconnect attempt,
 * so short-lived tokens (e.g. 30s WS-token-exchange) stay valid across drops.
 *
 * @param baseUrl - The WebSocket base URL
 * @param getToken - A function to asynchronously retrieve the WebSocket token
 * @param options - Event listeners and optional reconnect settings
 */
const useWebSocketWithToken = (
    baseUrl: string,
    getToken: () => Promise<string | null>,
    options: UseWebSocketWithTokenOptions = {}
): UseWebSocketWithTokenResult => {
    const { onOpen, onClose, onError, onMessage, reconnectInterval = 5000 } = options;

    const getTokenRef = useRef(getToken);
    useEffect(() => {
        getTokenRef.current = getToken;
    }, [getToken]);

    const getSocketUrl = useCallback(async (): Promise<string> => {
        const token = await getTokenRef.current();
        if (!token) {
            throw new Error('WebSocket token unavailable');
        }
        return `${baseUrl}?token=${token}`;
    }, [baseUrl]);

    const {
        readyState,
        getWebSocket,
        lastMessage,
    } = useWebSocket(getSocketUrl, {
        shouldReconnect: () => true,
        reconnectAttempts: 10,
        reconnectInterval,
        onOpen: () => onOpen && onOpen(),
        onClose: () => onClose && onClose(),
        onError: (event) => onError && onError(event),
    });

    useEffect(() => {
        if (lastMessage) {
            onMessage?.(JSON.parse(lastMessage?.data) as MessageDTO);
        }
    }, [lastMessage, onMessage]);

    const closeConnection = useCallback(() => {
        const ws = getWebSocket();
        if (ws) ws.close();
    }, [getWebSocket]);

    const isConnected = readyState === ReadyState.OPEN;

    return {
        isConnected,
        closeConnection,
    };
};

export default useWebSocketWithToken;
